/**
 * Classification of hls.js `ERROR` event payloads.
 *
 * hls.js reports every failure through one event with a loosely typed payload:
 * the HTTP status lives in a different field depending on the loader, and the
 * only thing distinguishing a DRM rejection from a missing playlist is the
 * `details` string. This module answers "what does this error mean"; deciding
 * what to do about it (recover, reload, surface a message) stays with the
 * player component.
 */

export type HlsErrorData = {
  details?: string;
  response?: {
    code?: number;
    status?: number;
  };
  networkDetails?: {
    status?: number;
    response?: {
      status?: number;
    };
  };
};

/**
 * hls.js `ErrorDetails` values for a playlist request that never produced a
 * usable playlist. String literals rather than the `Hls.ErrorDetails` enum so
 * this stays importable without pulling hls.js in; the enum members resolve to
 * exactly these strings.
 */
const PLAYLIST_LOAD_ERROR_DETAILS = [
  'manifestLoadError',
  'levelLoadError',
  'audioTrackLoadError',
];

/** hls.js `ErrorDetails` values that indicate an EME/DRM failure. */
const DRM_ERROR_DETAILS = [
  'keySystemNoKeys',
  'keySystemNoAccess',
  'keySystemNoSession',
  'keySystemNoConfiguredLicense',
  'keySystemLicenseRequestFailed',
  'keySystemServerCertificateRequestFailed',
  'keySystemServerCertificateUpdateFailed',
  'keySystemSessionUpdateFailed',
  'keySystemStatusOutputRestricted',
  'keySystemStatusInternalError',
  'keyLoadError',
  'keyLoadTimeOut',
  'keySystemError',
];

/**
 * Pull the HTTP status out of an hls.js error payload. Which field carries it
 * depends on the loader in use, so check every shape hls.js populates.
 */
export function getHlsErrorStatusCode(
  data: HlsErrorData | null | undefined,
): number | null {
  return data?.response?.code ??
    data?.response?.status ??
    data?.networkDetails?.status ??
    data?.networkDetails?.response?.status ??
    null;
}

/**
 * True when a playlist request failed with a 404, i.e. the source is expected
 * to be playable but its playlist is not on the CDN yet.
 *
 * The live control plane marks a stream playable before the packager has
 * written its playlists: the API flips a stream to Running (and a preview
 * player renders Created/Scheduled streams) while the CDN still 404s
 * `live.m3u8`. hls.js has no recovery for this - after a failed multivariant
 * manifest load there is no level to resume, so `startLoad()` requests nothing
 * and playback dead-ends until the page is reloaded.
 *
 * Deliberately narrow: other failures (403 token rejections, 5xx, timeouts)
 * keep hls.js's own handling so a genuinely broken source cannot turn into an
 * endless reload loop.
 */
export function isPlaylistNotFoundError(
  data: HlsErrorData | null | undefined,
): boolean {
  if (!data?.details) return false;
  if (!PLAYLIST_LOAD_ERROR_DETAILS.includes(data.details)) return false;
  return getHlsErrorStatusCode(data) === 404;
}

/** True when the error came from the EME/DRM pipeline rather than delivery. */
export function isDrmError(errorDetails: string | null | undefined): boolean {
  if (!errorDetails) return false;
  return DRM_ERROR_DETAILS.includes(errorDetails);
}
